import React from 'react';
import PageLoader from '../components/ui/PageLoader';

type ComponentModule = Record<string, React.ComponentType<any>>;

function lazyNamed<M extends ComponentModule, K extends keyof M>(
  loader: () => Promise<M>,
  exportName: K
) {
  return React.lazy(() =>
    loader().then((mod) => ({
      default: mod[exportName] as React.ComponentType<any>,
    }))
  );
}

function lazyDefault(loader: () => Promise<{ default: React.ComponentType<any> }>) {
  return React.lazy(loader);
}

export const LazyAdminLayout = lazyNamed(() => import('../components/AdminLayout'), 'AdminLayout');
export const LazyAdminLandingPages = lazyDefault(() => import('../components/AdminLandingPages'));
export const LazyAdminDashboard = lazyDefault(() => import('../components/AdminDashboard'));
export const LazyUserManagement = lazyNamed(() => import('../components/UserManagement'), 'UserManagement');
export const LazyRegistrationCodeManagement = lazyNamed(
  () => import('../components/RegistrationCodeManagement'),
  'RegistrationCodeManagement'
);
export const LazyActivityLog = lazyNamed(() => import('../components/ActivityLog'), 'ActivityLog');
export const LazyAdminEvents = lazyDefault(() => import('../components/AdminEvents'));
export const LazyAdminAiDashboard = lazyNamed(() => import('../components/AdminAiDashboard'), 'AdminAiDashboard');
export const LazyAdminClubsReview = lazyDefault(() => import('../components/AdminClubsReview'));
export const LazyAdminVehicleCertifications = lazyDefault(() => import('../components/AdminVehicleCertifications'));
export const LazyAdminFeaturedBuilds = lazyDefault(() => import('../components/AdminFeaturedBuilds'));
export const LazyAdminSponsorship = lazyDefault(() => import('../components/AdminSponsorship'));
export const LazyAdminProductUpdates = lazyDefault(() => import('../components/AdminProductUpdates'));
export const LazyAdminTestimonials = lazyDefault(() => import('../components/AdminTestimonials'));
export const LazyAdminAchievements = lazyDefault(() => import('../components/AdminAchievements'));
export const LazyAdminPerformanceModeration = lazyDefault(() => import('../components/AdminPerformanceModeration'));
export const LazyAdminFactorySpecs = lazyDefault(() => import('../components/AdminFactorySpecs'));
export const LazyAdminBuildMedia = lazyDefault(() => import('../components/AdminBuildMedia'));
export const LazyAdminManagedBuilders = lazyDefault(() => import('../components/AdminManagedBuilders'));
export const LazyAdminShopModeration = lazyDefault(() => import('../components/AdminShopModeration'));
export const LazyAdminPrivacyRequests = lazyDefault(() => import('../components/AdminPrivacyRequests'));
export const LazyAdminPayments = lazyDefault(() => import('../components/AdminPayments'));
export const LazyAdminPremium = lazyDefault(() => import('../components/AdminPremium'));
export const LazyAdminBadgeModeration = lazyDefault(() => import('../components/AdminBadgeModeration'));
export const LazyAdminPassport = lazyDefault(() => import('../components/AdminPassport'));
export const LazyAdminEmail = lazyDefault(() => import('../components/AdminEmail'));
export const LazyAdminMaps = lazyDefault(() => import('../components/AdminMaps'));
export const LazyAdminLimitedDesignations = lazyDefault(() => import('../components/AdminLimitedDesignations'));
export const LazyAdminFeatureFlags = lazyDefault(() => import('../components/AdminFeatureFlags'));

export const LazyOrganizerLayout = lazyDefault(() => import('../components/organizer/OrganizerLayout'));
export const LazyOrganizerDashboard = lazyDefault(() => import('../components/organizer/OrganizerDashboard'));
export const LazyCreateEventForm = lazyDefault(() => import('../components/organizer/CreateEventForm'));
export const LazyEventAdmin = lazyDefault(() => import('../components/organizer/EventAdmin'));
export const LazyEventCheckin = lazyDefault(() => import('../components/organizer/EventCheckin'));
export const LazyGateScanPage = lazyDefault(() => import('../components/organizer/GateScanPage'));
export const LazyOrganizerPaymentsPage = lazyDefault(() => import('../components/organizer/OrganizerPaymentsPage'));

export const LazyJudgeLayout = lazyDefault(() => import('../components/judge/JudgeLayout'));
export const LazyJudgeDashboard = lazyDefault(() => import('../components/judge/JudgeDashboard'));
export const LazyJudgeEventVehicles = lazyDefault(() => import('../components/judge/JudgeEventVehicles'));
export const LazyJudgeScorecard = lazyDefault(() => import('../components/judge/JudgeScorecard'));

export function OpsRouteFallback() {
  return <PageLoader message="Loading…" />;
}
